import React, { useState, useEffect, useCallback } from 'react';
import { Loading, EmptyState } from '../components/ui';

const API = '/api';
function authHeaders() {
  const token = localStorage.getItem('lc_token');
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

const C = {
  grn: '#3B6D11', grnBg: '#EAF3DE',
  amb: '#854F0B', ambBg: '#FAEEDA',
  red: '#A32D2D', redBg: '#FCEBEB',
  acc: '#185FA5', accBg: '#E6F1FB',
  border: 'rgba(0,0,0,0.08)', bg: '#F2F0EB', card: '#fff',
  txt: '#1a1a1a', txt2: '#666'
};

function Badge({ children, color, bg }) {
  return <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4, background: bg, color }}>{children}</span>;
}

// ─── Mini sparkline bar chart ─────────────────────────────────────────────────
function SparkBar({ data }) {
  if (!data || data.length === 0) return <span style={{ fontSize: 10, color: C.txt2 }}>No data</span>;
  const max = Math.max(...data.map(d => d.avg_skill_pct), 100);
  const months = data.map(d => {
    const dt = new Date(d.month);
    return `${dt.toLocaleString('default', { month: 'short' })} ${dt.getFullYear().toString().slice(2)}`;
  });
  const width = 200;
  const height = 48;
  const barW = Math.max(Math.floor((width - (data.length - 1) * 3) / data.length), 6);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <svg width={width} height={height} style={{ display: 'block' }}>
        {data.map((d, i) => {
          const barH = Math.round((d.avg_skill_pct / max) * (height - 10));
          const x = i * (barW + 3);
          const y = height - barH;
          const color = d.avg_skill_pct >= 80 ? C.grn : d.avg_skill_pct >= 60 ? C.amb : C.red;
          return (
            <g key={i}>
              <rect x={x} y={y} width={barW} height={barH} fill={color} rx={2} opacity={0.8} />
              <title>{months[i]}: {d.avg_skill_pct}%</title>
            </g>
          );
        })}
        {/* Trend line */}
        {data.length > 1 && (
          <polyline
            points={data.map((d, i) => {
              const x = i * (barW + 3) + barW / 2;
              const y = height - Math.round((d.avg_skill_pct / max) * (height - 10));
              return `${x},${y}`;
            }).join(' ')}
            fill="none" stroke={C.acc} strokeWidth={1.5} strokeDasharray="3,2" opacity={0.7}
          />
        )}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: C.txt2 }}>
        <span>{months[0]}</span>
        <span>{months[months.length - 1]}</span>
      </div>
    </div>
  );
}

// ─── Improved today card ──────────────────────────────────────────────────────
function ImprovedCard({ emp, trend }) {
  const delta = emp.new_skill_pct - (emp.prev_skill_pct || 0);
  return (
    <div style={{ background: C.card, borderRadius: 8, padding: '12px 14px', border: `0.5px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.txt }}>{emp.name}</div>
          <div style={{ fontSize: 10, color: C.txt2 }}>{emp.emp_code} · Line {emp.line_number}</div>
        </div>
        <Badge color={C.grn} bg={C.grnBg}>+{Math.round(delta)}%</Badge>
      </div>
      <div style={{ fontSize: 11, color: C.txt2 }}>{emp.operation_name}</div>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: C.txt2 }}>{emp.prev_skill_pct ? `${Math.round(emp.prev_skill_pct)}%` : 'New'}</span>
        <span style={{ color: C.txt2 }}>→</span>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.grn }}>{Math.round(emp.new_skill_pct)}%</span>
      </div>
      {trend && trend.length > 0 && <SparkBar data={trend} />}
    </div>
  );
}

// ─── Stagnant operator row ────────────────────────────────────────────────────
function StagnantRow({ emp, trend }) {
  const lastUpdated = emp.last_updated ? new Date(emp.last_updated) : null;
  const monthsAgo = lastUpdated
    ? Math.floor((Date.now() - lastUpdated.getTime()) / (1000 * 60 * 60 * 24 * 30))
    : null;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 80px 60px 80px 200px', gap: 10, padding: '10px 14px', borderBottom: `0.5px solid ${C.border}`, alignItems: 'center', background: C.card }}>
      <div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{emp.name}</div>
        <div style={{ fontSize: 10, color: C.txt2 }}>{emp.emp_code} · Line {emp.line_number}</div>
      </div>
      <div style={{ fontSize: 11, color: C.txt2, textAlign: 'center' }}>{emp.ops_known} ops</div>
      <div style={{ textAlign: 'center' }}>
        <Badge color={C.red} bg={C.redBg}>{monthsAgo != null ? `${monthsAgo}m` : '—'}</Badge>
      </div>
      <div style={{ fontSize: 10, color: C.txt2, textAlign: 'center' }}>
        {lastUpdated ? lastUpdated.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : 'Never'}
      </div>
      <div>{trend && trend.length > 0 ? <SparkBar data={trend} /> : <span style={{ fontSize: 10, color: C.txt2 }}>No training history</span>}</div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
export default function SkillJourney() {
  const [filters, setFilters] = useState({ factories: [], lines: [], divisions: [] });
  const [selectedFactory, setSelectedFactory] = useState('');
  const [selectedLine, setSelectedLine] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('improved'); // 'improved' | 'stagnant'

  useEffect(() => {
    const el = document.getElementById('lc-page-title');
    if (el) el.textContent = 'Skill Journey';
    fetch(`${API}/skill-matrix/filters`, { headers: authHeaders() })
      .then(r => r.json()).then(setFilters).catch(() => {});
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (selectedLine) params.append('shift_line_id', selectedLine);
    else if (selectedFactory) params.append('factory_id', selectedFactory);
    else if (selectedDivision) params.append('division_id', selectedDivision);
    fetch(`${API}/skill-matrix/journey?${params}`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [selectedDivision, selectedFactory, selectedLine]);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredFactories = selectedDivision
    ? (filters.factories || []).filter(f => String(f.division_id) === String(selectedDivision))
    : (filters.factories || []);
  const filteredLines = (filters.lines || []).filter(l =>
    (!selectedFactory || String(l.factory_id) === String(selectedFactory))
  );

  const { improved_today = [], stagnant = [], trend = {} } = data || {};

  return (
    <div style={{ padding: 24, background: C.bg, minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Skill Journey</div>
          <div style={{ fontSize: 12, color: C.txt2, marginTop: 2 }}>Operator skill improvement tracking & stagnation alerts</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <select value={selectedDivision} onChange={e => { setSelectedDivision(e.target.value); setSelectedFactory(''); setSelectedLine(''); }}
            style={{ padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 12 }}>
            <option value="">All divisions</option>
            {(filters.divisions || []).map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <select value={selectedFactory} onChange={e => { setSelectedFactory(e.target.value); setSelectedLine(''); }}
            style={{ padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 12 }}>
            <option value="">All factories</option>
            {filteredFactories.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <select value={selectedLine} onChange={e => setSelectedLine(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 12 }}>
            <option value="">All lines</option>
            {filteredLines.map(l => <option key={l.id} value={l.id}>Line {l.line_number}</option>)}
          </select>
          <button onClick={loadData} style={{ padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${C.border}`, background: C.card, fontSize: 12, cursor: 'pointer', color: C.acc, fontWeight: 600 }}>↺ Refresh</button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 18 }}>
        <div style={{ background: C.card, borderRadius: 8, padding: '12px 18px', border: `0.5px solid ${C.border}`, flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.txt2, textTransform: 'uppercase', marginBottom: 4 }}>Improved today</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.grn }}>{improved_today.length}</div>
          <div style={{ fontSize: 10, color: C.txt2 }}>operators with skill gain</div>
        </div>
        <div style={{ background: C.card, borderRadius: 8, padding: '12px 18px', border: `0.5px solid ${C.border}`, flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.txt2, textTransform: 'uppercase', marginBottom: 4 }}>Stagnant &gt; 6 months</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: stagnant.length > 0 ? C.red : C.grn }}>{stagnant.length}</div>
          <div style={{ fontSize: 10, color: C.txt2 }}>operators need attention</div>
        </div>
        <div style={{ background: C.card, borderRadius: 8, padding: '12px 18px', border: `0.5px solid ${C.border}`, flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.txt2, textTransform: 'uppercase', marginBottom: 4 }}>Avg improvement</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.acc }}>
            {improved_today.length > 0
              ? `+${Math.round(improved_today.reduce((a, e) => a + (e.new_skill_pct - (e.prev_skill_pct || 0)), 0) / improved_today.length)}%`
              : '—'}
          </div>
          <div style={{ fontSize: 10, color: C.txt2 }}>avg skill gain today</div>
        </div>
        <div style={{ background: C.card, borderRadius: 8, padding: '12px 18px', border: `0.5px solid ${C.border}`, flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: C.txt2, textTransform: 'uppercase', marginBottom: 4 }}>Trend data</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: C.acc }}>{Object.keys(trend).length}</div>
          <div style={{ fontSize: 10, color: C.txt2 }}>operators with 6m history</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
        {[['improved', `Improved Today (${improved_today.length})`], ['stagnant', `Stagnant > 6 months (${stagnant.length})`]].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding: '8px 18px', borderRadius: '6px 6px 0 0', border: 'none',
            background: tab === key ? C.card : 'transparent',
            borderBottom: tab === key ? `2px solid ${C.acc}` : '2px solid transparent',
            color: tab === key ? C.acc : C.txt2, fontSize: 13, fontWeight: 700, cursor: 'pointer'
          }}>{label}</button>
        ))}
      </div>

      {loading && <Loading />}

      {/* Improved today */}
      {!loading && tab === 'improved' && (
        <>
          {improved_today.length === 0 && (
            <EmptyState message="No skill improvements recorded today. Skill% updates when cycle time is saved in hourly reporting." />
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {improved_today.map((emp, i) => (
              <ImprovedCard key={i} emp={emp} trend={trend[emp.id]} />
            ))}
          </div>
        </>
      )}

      {/* Stagnant operators */}
      {!loading && tab === 'stagnant' && (
        <>
          {stagnant.length === 0 && (
            <EmptyState message="No stagnant operators found. All operators have been trained within the last 6 months." />
          )}
          {stagnant.length > 0 && (
            <div style={{ background: C.card, borderRadius: 8, border: `0.5px solid ${C.border}`, overflow: 'hidden' }}>
              {/* Header */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 80px 60px 80px 200px', gap: 10, padding: '8px 14px', background: '#F0EDE8', borderBottom: `1px solid ${C.border}` }}>
                {['Operator', 'Ops known', 'Stagnant', 'Last updated', '6-month trend'].map(h => (
                  <div key={h} style={{ fontSize: 9, fontWeight: 700, color: C.txt2, textTransform: 'uppercase' }}>{h}</div>
                ))}
              </div>
              {stagnant.map((emp, i) => (
                <StagnantRow key={emp.id} emp={emp} trend={trend[emp.id]} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
